/**
 * Les trois appels de la traduction du fil.
 *
 * ⛔ **On n'envoie JAMAIS la langue du fan.** Le cockpit dit la SIENNE — sa
 * locale i18next, que lui seul connaît — et le serveur résout celle du fan.
 * Laisser le front nommer la cible d'un envoi, ce serait laisser un bug d'ici
 * écrire à un fan italien en espagnol, et personne ne le verrait avant le fan.
 *
 * 🪤 Rien ici n'ENVOIE de message. `traduireLeTexte` rend un texte, l'aperçu
 * le montre, et le message part par le `useSendMessage` habituel — un seul
 * chemin d'envoi vers Telegram, celui qui porte déjà le garde anti-liens et
 * le mark-as-read.
 */

import { api as cyranoApi } from "@/core/api/client";

/** D'où le serveur tient la langue du fan : lue dans ce qu'il a ÉCRIT, ou —
 *  quand il n'a pas encore assez écrit — dans la locale de son Telegram. La
 *  seconde peut se tromper (un fan français dont le téléphone est en
 *  anglais), et le bandeau le dit. Absent d'un backend d'avant le 16-09-2026. */
export type SourceDeLangue = "texte" | "telegram";

/** Ce que le serveur sait de la langue du fan. `null` = il ne sait pas encore,
 *  et c'est une réponse exacte : le fan vient d'arriver. */
export interface LangueDuFan {
  langue: string | null;
  source?: SourceDeLangue | null;
}

export type SensDeTraduction = "comprendre" | "envoyer";

export interface TexteTraduit {
  /** La traduction, montants et liens restitués au caractère près. */
  texte: string;
  /** L'original. Il reste à l'écran — il ne doit jamais disparaître. */
  source: string;
  depuis: string | null;
  vers: string;
  modele: string;
  /** Le texte était DÉJÀ dans la langue d'arrivée : aucun modèle n'a tourné,
   *  `texte` est `source` au caractère près, et il n'y a rien à relire. Absent
   *  d'un backend d'avant le 15-09-2026 — d'où l'optionnel : le cockpit se
   *  déploie avant Cyrano, et un `undefined` se lit « traduit ». */
  deja_dans_la_langue?: boolean;
  /** Qui a traduit : `humain` (la version du script « ANGLAIS — … » que Kayla
   *  tient à la main), `llm` (le traducteur), ou `""` quand rien n'a été
   *  traduit. Absent d'un backend d'avant le 16-09-2026. */
  provenance?: string;
}

export interface EntrantsTraduits {
  langue: string;
  /** id de message → texte traduit. Un id absent n'est pas une erreur : le
   *  message n'avait rien à traduire, ou son appel a échoué — le fil garde
   *  l'original à l'écran, ce qu'il fait de toute façon. */
  traductions: Record<string, string>;
  echecs: Record<string, string>;
}

const racine = (conversationId: string) =>
  `/api/conversations/${encodeURIComponent(conversationId)}/traduction`;

/** Dans quelle langue ce fan écrit. */
export async function lireLaLangueDuFan(
  conversationId: string,
): Promise<LangueDuFan> {
  return cyranoApi.get<LangueDuFan>(`${racine(conversationId)}/langue`);
}

/** Traduit un texte libre — l'entrant qu'on veut relire, ou le message qu'on
 *  s'apprête à envoyer. */
export async function traduireLeTexte(
  conversationId: string,
  corps: {
    texte: string;
    sens: SensDeTraduction;
    langue_du_chatteur: string;
  },
): Promise<TexteTraduit> {
  return cyranoApi.post<TexteTraduit>(racine(conversationId), corps);
}

/** Traduit les entrants demandés. Le serveur les MÉMORISE : rouvrir la même
 *  conversation ne repaie rien, et le chatteur suivant non plus. */
export async function traduireLesEntrants(
  conversationId: string,
  corps: { ids: string[]; langue_du_chatteur: string },
): Promise<EntrantsTraduits> {
  return cyranoApi.post<EntrantsTraduits>(
    `${racine(conversationId)}/messages`,
    corps,
  );
}
